import { Link } from 'react-router-dom';
import StructuredData from '@/components/base/StructuredData';
import { categories, hotArticles, communityFeatures, communityStats } from '@/mocks/community';

export default function Home() {
  const websiteSchema = {
    url: window.location.origin,
    name: '海角社区',
    description: '海角社区是一个开放、包容的在线社交社区平台，汇聚来自各地的用户分享生活、交流情感、结交朋友。',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${window.location.origin}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    name: '海角社区',
    url: window.location.origin,
    description: '海角社区是一个开放、包容的在线社交社区平台。',
    sameAs: [],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'hello@haijiao.community',
      contactType: 'customer service',
    },
  };

  return (
    <div className="min-h-screen">
      <StructuredData type="WebSite" data={websiteSchema} />
      <StructuredData type="Organization" data={orgSchema} />

      {/* Hero */}
      <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20sunset%20coastline%20with%20golden%20light%20spreading%20across%20calm%20ocean%2C%20soft%20clouds%20in%20pastel%20orange%20and%20pink%20hues%2C%20peaceful%20seaside%20atmosphere%2C%20people%20walking%20along%20shore%20in%20distance%2C%20dreamy%20cinematic%20photography%20with%20warm%20glow%2C%20expansive%20horizon%2C%20serene%20and%20inviting%20mood&width=1800&height=900&seq=hero-bg-01&orientation=landscape"
          alt="海角社区温暖交流平台"
          title="海角社区——连接你我的温暖交流平台"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>

        <div className="relative z-10 w-full px-4 md:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 mb-6">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-compass-3-fill text-accent-400 text-sm"></i>
            </div>
            <span className="text-sm text-white/90 font-medium">连接你我的温暖交流平台</span>
          </div>

          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            在<span className="text-accent-400">海角</span>,遇见
            <br className="md:hidden" />
            志同道合的人
          </h1>
          <p className="text-base md:text-lg text-white/75 max-w-2xl mx-auto mb-8 leading-relaxed">
            海角社区汇聚来自各地的用户分享生活、交流情感、结交朋友。
            加入我们，发现真实而有温度的社区互动氛围。
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/categories"
              className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors duration-200"
            >
              开始探索
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link
              to="/about"
              className="px-8 py-3 rounded-full border border-white/40 text-white font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-white/10 transition-colors duration-200"
            >
              了解更多
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {communityStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                <div className="text-xs md:text-sm text-white/60 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topic Categories */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">探索话题分类</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              八大主题板块,涵盖生活的方方面面,找到你感兴趣的话题,开启精彩社区之旅
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="group cursor-pointer bg-background-50 rounded-xl p-4 md:p-5 hover:bg-primary-50 transition-colors duration-200 border border-background-200/60 hover:border-primary-200"
              >
                <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors duration-200 mb-3">
                  <i className={`${cat.icon} text-lg md:text-xl`}></i>
                </div>
                <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-1.5">{cat.name}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed mb-2 line-clamp-2">{cat.description}</p>
                <span className="text-xs text-foreground-400">{cat.articleCount} 篇文章</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Articles */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">热门文章</h2>
              <p className="text-sm text-foreground-600">社区精选,大家都在看的好内容</p>
            </div>
            <Link
              to="/categories"
              className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
            >
              查看全部
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {hotArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group cursor-pointer bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-200"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/85 backdrop-blur-sm text-xs font-medium text-foreground-700">
                    {article.categoryName}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-3 line-clamp-2">{article.summary}</p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <div className="flex items-center gap-2">
                      <img src={article.authorAvatar} alt={article.author} className="w-5 h-5 rounded-full object-cover" />
                      <span>{article.author}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line text-xs"></i>
                        {article.readCount >= 1000 ? `${(article.readCount / 1000).toFixed(1)}k` : article.readCount}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-heart-line text-xs"></i>
                        {article.likeCount}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Community Features */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">为什么选择海角社区</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              我们致力于打造一个温暖、安全、有趣的交流空间
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {communityFeatures.map((feature) => (
              <div
                key={feature.id}
                className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/60"
              >
                <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                  <i className={`${feature.icon} text-lg md:text-xl`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20sunset%20over%20ocean%20horizon%20with%20soft%20golden%20and%20coral%20sky%2C%20gentle%20waves%2C%20peaceful%20coastline%2C%20dreamy%20atmosphere%2C%20hopeful%20and%20inviting%20mood%2C%20inspirational%20landscape%20photography%20with%20warm%20ambient%20glow&width=1800&height=600&seq=cta-bg-01&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/35"></div>

        <div className="relative z-10 w-full px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-4">准备好加入我们了吗?</h2>
          <p className="text-base md:text-lg text-white/80 mb-8 max-w-lg mx-auto">
            无论你是想分享故事、学习技能,还是寻找志同道合的朋友,海角社区都欢迎你的到来。
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-accent-500 text-foreground-950 font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-accent-400 transition-colors duration-200"
          >
            立即加入海角社区
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}